import { tallyTokens } from "../../theme.tsx";
import "../../styles/conversation-drawer.css";
import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import { useDispatch } from "react-redux";
import { fetchAvailableModels } from "../../store/conversation.tsx";
import ConversationDrawerActions from "./ConversationDrawerActions.tsx";
import ConversationDrawerSection from "./ConversationDrawerSection.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";
import { getHasReadyConversation } from "../../store/conversation.tsx";
import { Dashboard } from "../../store/types.tsx";
import CloseDrawerIcon from "../icons/CloseDrawerIcon.tsx";
import TallyMark from "../../assets/tally-brown.png";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { TOP_BAR_HEIGHT } from "./TopBar.tsx";
import { CONVERSATION_LAYOUT } from "./constants.ts";
import { Box, useMediaQuery } from "@mui/material";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import LogoutDrawerAction from "./LogoutDrawerAction.tsx";
import DashboardDrawerSection from "./DashboardDrawerSection.tsx";
import PartnerCompanySwitcher from "./PartnerCompanySwitcher.tsx";

interface AppBarProps extends MuiAppBarProps {
  openDrawer?: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  onShowIntegrations?: () => void;
  onShowMemories?: () => void;
  onConversationSelected?: () => void;
}

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "block",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  zIndex: 2,
  backgroundColor: "inherit",
  flexShrink: 0,
}));

const EMPTY_DASHBOARDS: Dashboard[] = [];

const NO_OP = () => {};

const ConversationDrawer: React.FC<AppBarProps> = (props) => {
  const theme = useTheme();
  const conversations = useTypedSelector(
    (state) => state.conversationState.conversations,
  );
  const dashboardIds = useTypedSelector(
    (state) => state.conversationState.dashboardIds,
  );
  const dashboardsById = useTypedSelector(
    (state) => state.conversationState.dashboardsById,
  );
  const dashboards = React.useMemo(() => {
    if (dashboardIds.length === 0) {
      return EMPTY_DASHBOARDS;
    }

    return dashboardIds.flatMap((dashboardId) => {
      const dashboard = dashboardsById[dashboardId];
      return dashboard ? [dashboard] : [];
    });
  }, [dashboardIds, dashboardsById]);
  const hasDashboardsFeature = useFeatureFlag("dashboards");
  const hasAuthenticatedSession = Boolean(
    localStorage.getItem("token") || localStorage.getItem("partnerToken"),
  );

  const dispatch = useDispatch();

  // From md up the main panel shifts over to make room for the drawer, so
  // picking something in it does not hide anything and the drawer can stay
  // out. Below md the drawer covers the panel, so it gets out of the way.
  const panelMakesRoomForDrawer: boolean = useMediaQuery(
    theme.breakpoints.up("md"),
  );

  const handleDrawerClose = () => {
    props.setOpenDrawer(false);
  };

  const closeDrawerOnSelect = panelMakesRoomForDrawer
    ? NO_OP
    : handleDrawerClose;

  React.useEffect(() => {
    // fetch available models
    dispatch(fetchAvailableModels() as any);
  }, []);

  // Announces a conversation becoming ready, so the state reaches
  // screen-reader users even though the drawer dot is otherwise silent.
  // Only the false -> true transition is announced, not the level itself -
  // otherwise every unrelated re-render would repeat the same message.
  const hasReadyConversation = useTypedSelector(getHasReadyConversation);
  const [readyAnnouncement, setReadyAnnouncement] = React.useState("");
  const wasReadyRef = React.useRef(hasReadyConversation);
  React.useEffect(() => {
    const wasReady = wasReadyRef.current;
    wasReadyRef.current = hasReadyConversation;
    if (!hasReadyConversation || wasReady) return;

    setReadyAnnouncement(
      i18n._(
        msg({
          context:
            "Screen-reader announcement when a background answer finishes",
          message: "An answer is ready.",
        }),
      ),
    );
    const timeout = window.setTimeout(() => setReadyAnnouncement(""), 1000);
    return () => window.clearTimeout(timeout);
  }, [hasReadyConversation]);

  return (
    <Box>
      {/* Visually hidden; see the effect above for when this is set. */}
      <Box
        aria-live="polite"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {readyAnnouncement}
      </Box>
      <Drawer
        PaperProps={{
          sx: {
            backgroundColor: tallyTokens.surface2,
            color: tallyTokens.ink,
            borderRight: `1px solid ${tallyTokens.line}`,
            display: "flex",
            flexDirection: "column",
            overflowY: "hidden",
            overflowX: "hidden",
            paddingLeft: "14px",
            paddingRight: "14px",
            paddingBottom: 0,
          },
        }}
        sx={{
          height: "100vh",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: SETTINGS.drawerWidth,
            boxSizing: "border-box",
          },
          padding: 0,
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
        variant="persistent"
        anchor="left"
        open={props.openDrawer}
        id="conversation-drawer"
      >
        <Box
          sx={{
            zIndex: 3,
            backgroundColor: tallyTokens.surface2,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            paddingBottom: "6px",
          }}
        >
          <DrawerHeader
            sx={{
              // The same band as the main panel's top bar, so the logo and
              // the close button sit on one line with the bar's title.
              height: `${TOP_BAR_HEIGHT}px`,
              minHeight: `${TOP_BAR_HEIGHT}px !important`,
              boxSizing: "border-box",
              paddingTop: 0,
              paddingBottom: 0,
              // DrawerHeader carries 8px of side padding from theme.mixins.toolbar;
              // clearing it puts the logo on the same left edge (20px) as every
              // nav row, section header and conversation title.
              paddingLeft: "10px",
              paddingRight: "6px",
              display: "flex",
              alignItems: "center",
              gap: "9px",
            }}
          >
            <Box
              component="img"
              src={TallyMark}
              alt="Tally"
              sx={{
                height: "26px",
                width: "26px",
                borderRadius: "8px",
                display: "block",
                flexShrink: 0,
              }}
            />
            <Box
              component="span"
              sx={{
                fontSize: "19px",
                fontWeight: 600,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                color: tallyTokens.ink,
              }}
            >
              tally
            </Box>
            <IconButton
              onClick={handleDrawerClose}
              sx={{
                marginLeft: "auto",
                fontSize: theme.typography.h5.fontSize,
                marginRight: "-8px", // Compensate for IconButton padding
              }}
            >
              <CloseDrawerIcon color={tallyTokens.ink2} />
            </IconButton>
          </DrawerHeader>
          <ConversationDrawerActions
            closeDrawer={closeDrawerOnSelect}
            onShowIntegrations={props.onShowIntegrations}
            onShowMemories={props.onShowMemories}
            onConversationSelected={props.onConversationSelected}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minHeight: 0,
            paddingBottom: 1,
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarColor:
                "var(--tally-scroll-thumb) var(--tally-scroll-track)",
              flexGrow: 1,
              minHeight: 0,
            }}
            id="conversation-drawer-section-container"
          >
            {hasDashboardsFeature && (
              <DashboardDrawerSection
                dashboards={dashboards}
                closeDrawer={closeDrawerOnSelect}
                onDashboardSelected={props.onConversationSelected}
                title={i18n._(msg`Dashboards`)}
              />
            )}
            <ConversationDrawerSection
              conversations={conversations.pinned}
              closeDrawer={closeDrawerOnSelect}
              title={i18n._(
                msg({
                  context:
                    "Header for a section with conversations pinned by the user",
                  message: "Pinned",
                }),
              )}
              toggleConversation={false}
              onConversationSelected={props.onConversationSelected}
            />
            <ConversationDrawerSection
              conversations={conversations.last_7_days}
              closeDrawer={closeDrawerOnSelect}
              title={i18n._(
                msg({
                  context:
                    "Header for a section with conversations  created in the last 7 days",
                  message: "Last 7 days",
                }),
              )}
              toggleConversation={true}
              onConversationSelected={props.onConversationSelected}
            />
            <ConversationDrawerSection
              conversations={conversations.last_30_days}
              closeDrawer={closeDrawerOnSelect}
              title={i18n._(
                msg({
                  context:
                    "Header for a section with conversations  created in the last 30 days",
                  message: "Last 30 days",
                }),
              )}
              toggleConversation={true}
              onConversationSelected={props.onConversationSelected}
            />
            <ConversationDrawerSection
              conversations={conversations.other}
              closeDrawer={closeDrawerOnSelect}
              title={i18n._(
                msg({
                  context:
                    "Header for a section with conversations older than 30 days",
                  message: "Older",
                }),
              )}
              toggleConversation={true}
              onConversationSelected={props.onConversationSelected}
            />
          </Box>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            pt: "10px",
            pb: {
              xs: CONVERSATION_LAYOUT.bottomPadding.mobile,
              sm: CONVERSATION_LAYOUT.bottomPadding.tablet,
              md: CONVERSATION_LAYOUT.bottomPadding.desktop,
            },
            backgroundColor: tallyTokens.surface2,
            borderTop: `1px solid ${tallyTokens.line}`,
          }}
        >
          {hasAuthenticatedSession && <PartnerCompanySwitcher />}
          <LogoutDrawerAction closeDrawer={closeDrawerOnSelect} />
        </Box>
      </Drawer>
    </Box>
  );
};

export default ConversationDrawer;
